import { useSubmit } from "@certn/api";
import {
  BasicButton,
  ButtonGroup,
  PrimaryButton,
  Stack,
  Text,
  Link,
  showToast,
} from "@certn/cascadia";
import {
  Card,
  Container,
  Divider,
  GhostIconButton,
  Illustration,
  Layout,
  SectionMessage,
  Text as StepHeadingText,
} from "@certn/cascadia-react";
import { NavigationBar } from "@certn/cascadia-react/components/navigation/navigation-top/NavigationBar";
import { StepType } from "@certn/cascadia-react/components/stepper/ActivityStepper";
import { ProgressStepper } from "@certn/cascadia-react/components/stepper/ProgressStepper";
import { RegionContext, RegionProvider } from "@certn/utils";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CheckCardError } from "./components/CheckCardError";
import { OrderReviewSummaryBox } from "../../../components/ordering-review/OrderReviewSummaryBox";
import { newOrder, OrderContext } from "../OrderContext";
import { getOptionHasOrderingErrorForCustomerGroupFromChecks } from "../order-utils";
import { CheckData } from "../types";
import { useFocusStepHeading } from "../useFocusStepHeading";

function Package() {
  const { order } = useContext(OrderContext);

  return (
    <Stack gap="space-3" className={styles.package}>
      <RegionProvider>
        <RegionContext.Consumer>
          {(region) => (
            <OrderReviewSummaryBox
              order={order}
              withoutPriceInfo
              hideBorder
              region={region}
            />
          )}
        </RegionContext.Consumer>
      </RegionProvider>
      <Divider />
    </Stack>
  );
}

function NextSteps({ steps }: { steps: StepType[] }) {
  const { t } = useTranslation();

  return (
    <Stack gap="space-4" className={styles.nextSteps}>
      <Text style="form/page-title">{t("What happens next")}</Text>
      <div className={styles.stepper}>
        <ProgressStepper steps={steps} />
      </div>
    </Stack>
  );
}

export function NiceJobStep() {
  const navigate = useNavigate();
  const { clientPortalCreateNewPackage } = useFlags();
  const [packageLink, setPackageLink] = useState("");
  const { order, setOrder } = useContext(OrderContext);
  const { t } = useTranslation();
  const stepHeadingId = useFocusStepHeading();

  const { submit, isSubmitting } = useSubmit(
    `/api/internal/client-portal/packages/create/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: (data: { package_id: string }) => {
        showToast(t("Package created"), { type: "info" });
        setPackageLink(data.package_id);
      },
    }
  );

  const submitPackage = async () => {
    try {
      await submit({
        name: "",
        description: "",
        checks: order.checks.map((check) => ({
          check_type: check.key,
          arguments: check.check_arguments,
        })),
        bundles: order.bundles,
        tags: order.tags,
      });
    } catch (e) {
      console.error("Error", e);
    }
  };

  const defaultSteps: StepType[] = [
    {
      title: t("Recipient(s) will now receive their invitations"),
      description: t(
        "Recipients will receive reminder emails if they have any pending checks to complete."
      ),
      status: "ACTIVE",
    },
    {
      title: t("They will be guided through all required questions "),
      description: t(
        "Our team is always available to assist with any questions."
      ),
      status: "ACTIVE",
    },
    {
      title: t("Once complete, their reports will be available to view"),
      description: t(
        "You can check on the progress of the cases at any time here"
      ),
      status: "ACTIVE",
    },
  ];

  const errors = getOptionHasOrderingErrorForCustomerGroupFromChecks(
    order.checks
  );

  const [completionAnnouncement, setCompletionAnnouncement] = useState("");

  useEffect(() => {
    setCompletionAnnouncement(t("Your candidates have been emailed."));
  }, [t]);

  return (
    <Layout
      header={
        <NavigationBar testID="nice-job-step-navigation-bar">
          <ButtonGroup justify="start">
            <GhostIconButton icon="close" onPress={() => navigate("/cases")} />
          </ButtonGroup>
        </NavigationBar>
      }
    >
      <Container>
        <Stack gap="space-4">
          <Stack gap="space-0" align="center">
            <Illustration name="success" className={styles.illustration} />
            <StepHeadingText
              id={stepHeadingId}
              as="h1"
              tabIndex={-1}
              style="global/title-large"
            >
              <Trans>Nice job!</Trans>
            </StepHeadingText>
            <Text style="global/heading-large">
              <Trans>Your candidates have been emailed.</Trans>
            </Text>
            <div
              role="status"
              aria-live="polite"
              className={styles.visuallyHidden}
            >
              {completionAnnouncement}
            </div>
          </Stack>
          {errors.length > 0 && (
            <Stack gap="space-2">
              {errors.map(
                (
                  error:
                    | CheckData["check_has_ordering_error_for_customer_group"]
                    | null,
                  idx
                ) =>
                  error && (
                    <CheckCardError
                      key={`${error.type}-${idx}`}
                      error={error}
                    />
                  )
              )}
            </Stack>
          )}
          <Card border="dropshadow" className={styles.card}>
            <Stack gap="space-2">
              <Package />
              <ButtonGroup className={styles.buttonGroup}>
                <BasicButton
                  testID="view-cases"
                  onPress={() => navigate("/cases")}
                >
                  <Trans>View cases</Trans>
                </BasicButton>
                <PrimaryButton
                  testID="start-new-order"
                  onPress={() => {
                    setOrder(newOrder);
                    navigate("/order");
                  }}
                >
                  <Trans>Start new order</Trans>
                </PrimaryButton>
              </ButtonGroup>
            </Stack>
          </Card>
          {clientPortalCreateNewPackage && (
            <SectionMessage
              className={styles.sectionMessage}
              type="success"
              icon="package_2"
              sectionTitle={t(
                "Do you order these checks often?  Create a package!"
              )}
              children={t(
                "This will save you some time when you come to order again."
              )}
              actions={
                !packageLink ? (
                  <BasicButton
                    onPress={() => submitPackage()}
                    isDisabled={isSubmitting}
                  >
                    <Trans>Create</Trans>
                  </BasicButton>
                ) : (
                  <Link
                    textStyle="form/body-secondary-link"
                    testID="view-package"
                    linkStyle="monochrome"
                    href={`/package/${packageLink}`}
                  >
                    <Trans>View package</Trans>
                  </Link>
                )
              }
            />
          )}
          <NextSteps steps={defaultSteps} />
        </Stack>
      </Container>
    </Layout>
  );
}

const styles = {
  visuallyHidden: css({
    border: 0,
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: "1px",
    margin: "-1px",
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    width: "1px",
    whiteSpace: "nowrap",
  }),
  nextSteps: css({
    padding: "var(--space-4)",
  }),
  stepper: css({
    paddingLeft: "var(--space-4)",
  }),
  illustration: css({
    paddingBottom: "var(--space-2)",
  }),
  package: css({
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
  }),
  card: css({
    borderRadius: "var(--border-radius-3)",
    paddingTop: "var(--space-3)",
    paddingBottom: "var(--space-3)",
    paddingRight: "var(--space-4)",
    paddingLeft: "var(--space-4)",
  }),
  buttonGroup: css({
    paddingBottom: "var(--space-2)",
    "& > button": {
      flex: 1,
    },
  }),
  sectionMessage: css({
    alignItems: "center",
  }),
};
